import {
  LocationAcuityCode,
  LocationAcuityLabel,
} from '@quromedical/fhir-common';
import type { GetAcuityResponse } from '@quromedical/models';
import { Skeleton, SkeletonProvider, Text } from 'design-system';
import { useIsSmallerThan } from 'hooks/useResponsive';

import { Dot } from '../dot';
import {
  getAcuityColor,
  ItemRow,
  SummaryItem,
  WardContainer,
} from './internals';

type WardCountProps = {
  acuityCounts: GetAcuityResponse;
  isError: boolean;
  isLoading: boolean;
};

export const WardCount: React.FC<WardCountProps> = ({
  acuityCounts,
  isError,
  isLoading,
}: {
  acuityCounts: GetAcuityResponse;
  isError: boolean;
  isLoading: boolean;
}) => {
  const wardCountSummaries = Object.entries(acuityCounts ?? {})
    .map(([rawCode, rawCount]) => {
      const acuityCode = rawCode as LocationAcuityCode;
      const count = rawCount;
      const acuity = LocationAcuityLabel[acuityCode];
      return { acuity, count, acuityCode };
    })
    .sort((a, b) => {
      if (a.acuityCode === LocationAcuityCode.Unallocated) return 1;
      if (b.acuityCode === LocationAcuityCode.Unallocated) return -1;
      return 0;
    });

  const isSmall = useIsSmallerThan('lg');

  if (isError)
    return (
      <Text color={'status-critical'}>Error loading admission summaries.</Text>
    );

  return (
    <SkeletonProvider loading={isLoading}>
      <Skeleton borderRadius={8} fullWidth={false}>
        <WardContainer>
          {wardCountSummaries?.map(({ acuity, count, acuityCode }) => (
            <SummaryItem key={acuity}>
              <ItemRow>
                <Dot size={8} color={getAcuityColor(acuityCode)} />
                <Text
                  selectable={false}
                  color={'white'}
                  variant={isSmall ? 'body' : 'page-heading'}
                >
                  {count}
                </Text>
              </ItemRow>
              <Text
                selectable={false}
                color={'text-subdued'}
                variant={isSmall ? 'caption' : 'body'}
              >
                {acuity}
              </Text>
            </SummaryItem>
          ))}
        </WardContainer>
      </Skeleton>
    </SkeletonProvider>
  );
};
